import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDailyPnL, fetchCumulativePnL, fetchBetStats } from '../api/client';
import MetricCard from '../components/MetricCard';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';

export default function PnL() {
  const [days, setDays] = useState(30);

  const { data: dailyData } = useQuery({
    queryKey: ['pnl-daily', days],
    queryFn: () => fetchDailyPnL(days),
  });

  const { data: cumulativeData } = useQuery({
    queryKey: ['pnl-cumulative'],
    queryFn: fetchCumulativePnL,
  });

  const { data: statsData } = useQuery({
    queryKey: ['bet-stats'],
    queryFn: fetchBetStats,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">P&L Tracking</h1>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value={7}>7 Days</option>
          <option value={30}>30 Days</option>
          <option value={90}>90 Days</option>
        </select>
      </div>

      {/* Key Metrics */}
      {statsData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard
            label="Total P&L"
            value={formatPnL(statsData.summary.totalPnL)}
            trend={statsData.summary.totalPnL >= 0 ? 'up' : 'down'}
            icon="💰"
          />
          <MetricCard
            label="Win Rate"
            value={`${(statsData.summary.winRate * 100).toFixed(1)}%`}
            icon="🎯"
          />
          <MetricCard
            label="ROI"
            value={`${(statsData.summary.roi * 100).toFixed(2)}%`}
            trend={statsData.summary.roi >= 0 ? 'up' : 'down'}
            icon="📈"
          />
          <MetricCard
            label="Avg Win"
            value={formatPnL(statsData.summary.avgWin)}
            icon="🟢"
          />
          <MetricCard
            label="Avg Loss"
            value={formatPnL(statsData.summary.avgLoss)}
            icon="🔴"
          />
        </div>
      )}

      {/* Daily P&L Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Daily P&L</h2>
        <div className="h-72">
          {dailyData?.data && dailyData.data.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tickFormatter={(val) =>
                    new Date(val).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                />
                <YAxis stroke="#6b7280" tickFormatter={(val) => `$${val}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`$${value.toFixed(2)}`, 'P&L']}
                />
                <ReferenceLine y={0} stroke="#6b7280" />
                <Bar
                  dataKey="pnl"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                  // Color bars based on value
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  shape={(props: any) => {
                    const { x, y, width, height, payload } = props;
                    const fill = payload.pnl >= 0 ? '#22c55e' : '#ef4444';
                    return (
                      <rect
                        x={x}
                        y={payload.pnl >= 0 ? y : y + height}
                        width={width}
                        height={Math.abs(height)}
                        fill={fill}
                        rx={4}
                        ry={4}
                      />
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              No P&L data available for this period
            </div>
          )}
        </div>
      </div>

      {/* Cumulative P&L Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Cumulative P&L</h2>
        <div className="h-72">
          {cumulativeData?.data && cumulativeData.data.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cumulativeData.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tickFormatter={(val) =>
                    new Date(val).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                />
                <YAxis stroke="#6b7280" tickFormatter={(val) => `$${val}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) => [
                    `$${value.toFixed(2)}`,
                    name === 'cumulativePnL' ? 'Cumulative' : 'Daily',
                  ]}
                />
                <ReferenceLine y={0} stroke="#6b7280" />
                <Line
                  type="monotone"
                  dataKey="cumulativePnL"
                  stroke="#e94560"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              No cumulative P&L data available
            </div>
          )}
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Sport */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">P&L by Sport</h2>
          {statsData?.bySport && statsData.bySport.length > 0 ? (
            <div className="space-y-3">
              {statsData.bySport.map((item) => (
                <div
                  key={item.sport}
                  className="flex items-center justify-between p-3 bg-surface-50 rounded"
                >
                  <div>
                    <div className="font-medium">{item.sport}</div>
                    <div className="text-xs text-gray-400">{item.bets} bets</div>
                  </div>
                  <span
                    className={`font-mono font-medium ${
                      item.pnl >= 0 ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {formatPnL(item.pnl)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">No data available</p>
          )}
        </div>

        {/* By Signal Type */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">P&L by Signal Type</h2>
          {statsData?.bySignalType && statsData.bySignalType.length > 0 ? (
            <div className="space-y-3">
              {statsData.bySignalType.map((item) => (
                <div
                  key={item.signalType}
                  className="flex items-center justify-between p-3 bg-surface-50 rounded"
                >
                  <div>
                    <div className="font-medium text-sm">
                      {formatSignalType(item.signalType)}
                    </div>
                    <div className="text-xs text-gray-400">{item.bets} bets</div>
                  </div>
                  <span
                    className={`font-mono font-medium ${
                      item.pnl >= 0 ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {formatPnL(item.pnl)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">No data available</p>
          )}
        </div>
      </div>
    </div>
  );
}

function formatPnL(value: number): string {
  const prefix = value >= 0 ? '+$' : '-$';
  return `${prefix}${Math.abs(value).toFixed(2)}`;
}

function formatSignalType(type: string): string {
  const labels: Record<string, string> = {
    SHARP_VS_SQUARE: 'Sharp vs Square',
    STEAM_MOVE: 'Steam Move',
    CONSENSUS_DIVERGENCE: 'Consensus Div',
    OPENING_VS_CURRENT: 'Opening vs Current',
  };
  return labels[type] || type;
}
